import { navigate } from '@reach/router'
import { REFRESH_LEXON_TOKEN } from 'src/constants/rest'
import { ERROR_LEXNET_SESSION_EXPIRED } from 'src/constants/lexnet'
import * as routes from 'src/constants/routes'
import { setAppTokens } from 'src/state/authentication/actions'
import { refreshTokenSelector, tokenSelector } from 'src/state/authentication/selectors'
import store from 'src/state/store'
import { decrementPendingRequests, incrementPendingRequests } from 'src/utils/pendingRequests'
import { isLexnetActionUrl, notifyLexnetActivity } from 'src/utils/lexnetActivity'
import { triggerLexnetSessionExpired } from 'src/utils/lexnetSessionExpired'
import parseJwt from '../parseJwt'
import { unauthenticatedError } from './CommonMiddlewares'
import { ExtendedData, Http } from './Http'
import HttpError from './HttpError'

const http = new Http()

const THRESHOLD_TO_RENEW_TOKEN = 10 * 60 * 1000

async function refreshTokenMiddleware(data: ExtendedData) {
  if (data.token) {
    const token = tokenSelector(store.getState())
    if (!token) {
      navigate(routes.NO_AUTH)
      return Promise.resolve(data)
    }
    const refreshToken = refreshTokenSelector(store.getState())
    const parsedToken = parseJwt(token)
    const now = new Date()
    if (now.getTime() > parsedToken.exp * 1000 - THRESHOLD_TO_RENEW_TOKEN) {
      try {
        const headers = new Headers()
        headers.append('Content-type', 'application/json')
        headers.append('Authorization', `Bearer ${token}`)
        const response = await fetch(REFRESH_LEXON_TOKEN, {
          method: 'POST',
          body: JSON.stringify({ refreshToken }),
          headers
        })
        if (!response.ok) {
          navigate(routes.NO_AUTH)
          return Promise.reject()
        }
        const appTokens = await response.json()
        store.dispatch(setAppTokens(appTokens))
      } catch (error) {
        navigate(routes.NO_AUTH)
        return Promise.reject()
      }
    }
  }

  return Promise.resolve(data)
}

function tokenMiddleware(data: ExtendedData): Promise<ExtendedData> {
  data.headers = data.headers || {}
  data.headers['Content-type'] = 'application/json'
  data.headers.Accept = 'application/json'

  if (data.token) {
    const token = tokenSelector(store.getState())
    data.headers.Authorization = `Bearer ${token}`
  }

  return Promise.resolve(data)
}

function lexnetUnauthenticatedError(error: HttpError) {
  // eslint-disable-next-line no-console
  console.error('[DEBUG lexnetUnauthenticatedError]', {
    name: (error as any)?.name,
    status: (error as any)?.status,
    response: (error as any)?.response,
    raw: error
  })

  if (
    error.name === 'HttpError' &&
    Number(error.response?.code) === ERROR_LEXNET_SESSION_EXPIRED
  ) {
    triggerLexnetSessionExpired()
  }

  return Promise.resolve()
}

async function trackPendingRequestStart(data: ExtendedData) {
  incrementPendingRequests()
  return data
}

async function trackLexnetActivityStart(data: ExtendedData) {
  if (isLexnetActionUrl(data.url)) {
    notifyLexnetActivity()
  }
  return data
}

function trackLexnetActivityEnd(data: ExtendedData, succeeded: boolean) {
  if (succeeded && isLexnetActionUrl(data.url)) {
    notifyLexnetActivity()
  }
}

async function trackPendingRequestEnd(response: any) {
  decrementPendingRequests()
  return response
}

async function trackPendingRequestError(_error: HttpError) {
  decrementPendingRequests()
}

http.addMidleware('onRequest', tokenMiddleware)
http.addMidleware('onRequest', refreshTokenMiddleware)
http.addMidleware('onRequest', trackPendingRequestStart)
http.addMidleware('onRequest', trackLexnetActivityStart)
http.addMidleware('onResponse', trackPendingRequestEnd)
http.addMidleware('onError', unauthenticatedError)
http.addMidleware('onError', lexnetUnauthenticatedError)
http.addMidleware('onError', trackPendingRequestError)
http.addMidleware('onComplete', trackLexnetActivityEnd)

export default http
